import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Instagram, Facebook, Youtube, ShoppingBag } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { useCart } from "@/context/CartContext";

const links = [
  { to: "/#services", label: "Services", id: NAV.services },
  { to: "/#process", label: "Process", id: NAV.process },
  { to: "/#gallery", label: "Work", id: "nav-gallery-link" },
  { to: "/shop", label: "Shop", id: "nav-shop-link", route: true },
  { to: "/#booking", label: "Book", id: NAV.booking },
  { to: "/#contact", label: "Contact", id: NAV.contact },
];

const SOCIALS = [
  {
    href: "https://www.instagram.com/seventh_car_detailing",
    label: "Instagram",
    Icon: Instagram,
    id: "nav-social-instagram",
  },
  {
    href: "https://www.facebook.com/seven7hcardetailing",
    label: "Facebook",
    Icon: Facebook,
    id: "nav-social-facebook",
  },
  {
    href: "https://www.youtube.com/@SEVEN7HCARDEATILING",
    label: "YouTube",
    Icon: Youtube,
    id: "nav-social-youtube",
  },
];


export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const cart = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (e, href, isRoute) => {
    if (isRoute) {
      setOpen(false);
      return; // let react-router Link handle it
    }
    if (!href.startsWith("/#")) return;
    e.preventDefault();
    const id = href.replace("/#", "");
    // If we're on another route, navigate home first
    if (window.location.pathname !== "/") {
      window.location.href = "/" + "#" + id;
      return;
    }
    // Close the mobile menu, then scroll on the next frame
    // (menu-open state may briefly occupy the viewport and offset targets).
    setOpen(false);
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (!el) return;
      const navH = window.innerWidth >= 768 ? 80 : 64;
      // Prefer Lenis (used site-wide) so scrolling actually smooths on mobile;
      // fall back to native window.scrollTo when Lenis isn't ready.
      const lenis = window.__lenis;
      if (lenis && typeof lenis.scrollTo === "function") {
        lenis.scrollTo(el, { offset: -navH, duration: 1.1 });
      } else {
        const y = el.getBoundingClientRect().top + window.scrollY - navH;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    });
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#050505]/80 backdrop-blur-xl border-b border-white/5"
          : "bg-[#050505]/30 backdrop-blur-md"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid={NAV.logo}
          onClick={(e) => {
            e.preventDefault();
            setOpen(false);
            if (location.pathname !== "/") {
              navigate("/");
              // wait a tick for the home page to mount, then jump to top
              setTimeout(() => window.scrollTo({ top: 0, behavior: "auto" }), 50);
            } else {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
          className="flex items-baseline gap-3 text-white"
        >
          <span className="font-serif text-lg md:text-xl tracking-[0.4em] uppercase">
            Seven<span className="text-brand-red">7</span>h
          </span>
          <span className="hidden sm:inline font-mono text-[10px] tracking-[0.28em] uppercase text-neutral-500">
            Car Detailing
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) =>
            l.route ? (
              <Link
                key={l.label}
                to={l.to}
                data-testid={l.id}
                onClick={() => setOpen(false)}
                className="font-mono text-[11px] tracking-[0.28em] uppercase text-neutral-400 hover:text-white transition-colors"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.label}
                href={l.to}
                onClick={(e) => handleAnchor(e, l.to)}
                data-testid={l.id}
                className="font-mono text-[11px] tracking-[0.28em] uppercase text-neutral-400 hover:text-white transition-colors"
              >
                {l.label}
              </a>
            )
          )}
        </nav>

        <div className="flex items-center gap-3 md:gap-5">
          <div className="hidden md:flex items-center gap-3 pr-3 border-r border-white/10">
            {SOCIALS.map(({ href, label, Icon, id }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                data-testid={id}
                aria-label={label}
                className="text-neutral-400 hover:text-white transition-colors"
              >
                <Icon size={15} strokeWidth={1.4} />
              </a>
            ))}
          </div>
          <a
            href="tel:0429052189"
            className="hidden md:block font-mono text-[11px] tracking-[0.24em] uppercase text-neutral-400 hover:text-white transition-colors"
          >
            0429 052 189
          </a>
          <button
            type="button"
            onClick={() => cart?.setOpen(true)}
            data-testid="nav-cart-btn"
            aria-label="Open cart"
            className="relative w-10 h-10 flex items-center justify-center text-white hover:text-brand-red transition-colors"
          >
            <ShoppingBag size={16} strokeWidth={1.4} />
            {cart?.count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-red text-white font-mono text-[10px] flex items-center justify-center"
              >
                {cart.count}
              </span>
            )}
          </button>
          <a
            href="/#booking"
            onClick={(e) => handleAnchor(e, "/#booking")}
            data-testid={NAV.bookCta}
            className="btn-primary !py-2.5 !px-5 !text-[11px]"
          >
            Book Now
          </a>
          <button
            className="md:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid={NAV.mobileToggle}
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#050505]/95 backdrop-blur-xl border-t border-white/5"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) =>
                l.route ? (
                  <Link
                    key={l.label}
                    to={l.to}
                    data-testid={`mobile-${l.id}`}
                    onClick={() => setOpen(false)}
                    className="font-serif text-3xl text-white"
                  >
                    {l.label}
                  </Link>
                ) : (
                  <a
                    key={l.label}
                    href={l.to}
                    data-testid={`mobile-${l.id}`}
                    onClick={(e) => handleAnchor(e, l.to)}
                    className="font-serif text-3xl text-white"
                  >
                    {l.label}
                  </a>
                )
              )}
              <a
                href="tel:0429052189"
                className="font-mono text-xs tracking-[0.24em] uppercase text-neutral-400 mt-4"
              >
                0429 052 189
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
